import React, { useEffect, useRef, useState, useCallback } from 'react';
import { List, ListProps, Spin } from 'antd';
import { useDispatch } from 'react-redux';
import { getData } from '../../../actions/apiCall';
import { debounce } from 'lodash';

export interface InfiniteScrollListProps {
  apiUrl?: string;
  apiQuery?: any;
  renderItem?: (item: any, index: number, data?: any[]) => React.ReactNode;
  loader?: React.ReactNode;
  height?: number | undefined;
  itemsPerPage?: number;
  reload?: boolean;
  onLoaded?: () => void;
  listProps?: ListProps<any>;
}

const InfiniteScrollList: React.FC<InfiniteScrollListProps> = ({
  apiUrl,
  renderItem,
  loader = <Spin size="small" />,
  height,
  itemsPerPage = 10,
  reload,
  onLoaded,
  listProps,
  apiQuery
}) => {
  const [data, setData] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const dispatch = useDispatch();
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (reload) {
      setData([])
      setPage(1)
      setHasMore(true)
      setLoaded(false)
      onLoaded && onLoaded()
    }
  }, [reload, onLoaded]);


  // Function to load data
  const loadMoreData = useCallback(async () => {
    if (loading || !hasMore || !apiUrl) return;

    setLoading(true); // Mark loading as true when fetching data
    try {
      // Merge apiQuery with pagination params - support both page/limit and current/pageSize
      const queryParams = {
        current: page,
        pageSize: itemsPerPage,
        page: page,
        limit: itemsPerPage,
        ...apiQuery
      };
      const response = await dispatch(getData(apiUrl, queryParams));

      if (response) {
        let newData = response?.data;

        if (Array.isArray(newData)) {
        } else if (newData?.data && Array.isArray(newData.data)) {
          newData = newData.data;
        } else if (newData?.list && Array.isArray(newData.list)) {
          newData = newData.list;
        } else if (newData?.activities && Array.isArray(newData.activities)) {
          newData = newData.activities;
        } else {
          newData = [];
        }

        if (newData && Array.isArray(newData) && newData.length > 0) {
          setData((prevData) => {
            // Prevent duplicates by checking _id
            const existingIds = new Set(prevData.map(item => item._id || item.id));
            const uniqueNewData = newData.filter(item => {
              const id = item._id || item.id;
              return id && !existingIds.has(id);
            });
            return [...prevData, ...uniqueNewData];
          });
          if (newData.length < itemsPerPage) {
            setHasMore(false); // No more data to load
          }
          setPage((prev) => prev + 1); // Increment page after successful fetch
        } else {
          setHasMore(false); // No more data to load
        }
      } else {
        setHasMore(false); // No more data to load
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setHasMore(false); // Handle error, stop loading more
    } finally {
      setLoading(false); // Reset loading state
    }
  }, [dispatch, apiUrl, page, itemsPerPage, hasMore, loading, apiQuery]);

  // Scroll event handler with debounce (memoized to avoid re-creation on each render)
  const handleScroll = useCallback(
    debounce(() => {
      if (!containerRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 100 && !loading && hasMore) {
        loadMoreData(); // Trigger loading more data when reaching near the bottom
      }
    }, 300), [loadMoreData, loading, hasMore]); // Memoizing handleScroll to avoid re-creation

  // Reset when apiUrl or apiQuery changes
  useEffect(() => {
    setData([]);
    setPage(1);
    setHasMore(true);
    setLoaded(false);
  }, [apiUrl, JSON.stringify(apiQuery || {})]);

  // Initial load effect
  useEffect(() => {
    if (apiUrl && !loaded && hasMore && !loading) {
      const timer = setTimeout(() => {
        setLoaded(true);
        loadMoreData();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [apiUrl, loaded, hasMore, loading]); // Removed loadMoreData to avoid infinite loop

  // Scroll event listener effect
  useEffect(() => {
    const currentRef = containerRef.current;
    if (currentRef) {
      currentRef.addEventListener('scroll', handleScroll);
    }

    // Cleanup listener on component unmount
    return () => {
      if (currentRef) {
        currentRef.removeEventListener('scroll', handleScroll);
      }
    };
  }, [handleScroll]);

  return (
    <div
      ref={containerRef}
      style={{
        ...(height !== undefined ? { height: `${height}px`, overflow: 'auto' } : { height: '100%', overflow: 'auto', minHeight: 0 }),
      }}
      className='w-full'
    >
      {data.length > 0 ? (
        <List
          dataSource={data}
          renderItem={(item, index) => renderItem ? renderItem(item, index, data) : null}
          {...listProps}
        />
      ) : !loading ? (
        <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>No data found</div>
      ) : null}
      {loading && (
        <div style={{ textAlign: 'center', margin: '12px 0' }}>{loader}</div>
      )}
    </div>
  );
};

export default InfiniteScrollList;
